import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function formatMeasurementNumber(value: number): string {
  const absolute = Math.abs(value)
  if (absolute >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (absolute >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (absolute >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }
  return value.toLocaleString("en-US", {
    maximumFractionDigits: absolute < 100 ? 2 : 1,
  })
}

function formatFeetAndInchesFromInches(totalInches: number): string {
  let feet = Math.floor(totalInches / 12)
  let inches = Math.round(totalInches - feet * 12)
  if (inches === 12) {
    feet += 1
    inches = 0
  }
  return `${feet} ft ${inches} in`
}

function formatImperialLengthFromMetres(metres: number): string {
  const inches = metres * 39.3701
  return metres < 3
    ? formatFeetAndInchesFromInches(inches)
    : `${formatMeasurementNumber(metres * 3.28084)} ft`
}

function formatDualMeasurement(value: number, unit: string): string | null {
  const normalizedUnit = unit.trim().toLocaleLowerCase("en-US")
  const primary = `${formatMeasurementNumber(value)} ${unit}`

  const converted = (() => {
    switch (normalizedUnit) {
      case "mm":
      case "millimetre":
      case "millimetres":
      case "millimeter":
      case "millimeters":
        return `${formatMeasurementNumber(value / 25.4)} in`
      case "cm":
      case "centimetre":
      case "centimetres":
      case "centimeter":
      case "centimeters":
        return formatFeetAndInchesFromInches(value / 2.54)
      case "km":
      case "kilometre":
      case "kilometres":
      case "kilometer":
      case "kilometers":
        return `${formatMeasurementNumber(value * 0.621371)} mi`
      case "mi":
      case "mile":
      case "miles":
        return `${formatMeasurementNumber(value * 1.609344)} km`
      case "m":
      case "metre":
      case "metres":
      case "meter":
      case "meters":
        return formatImperialLengthFromMetres(value)
      case "ft":
      case "foot":
      case "feet":
        return `${formatMeasurementNumber(value * 0.3048)} m`
      case "in":
      case "inch":
      case "inches":
        return `${formatMeasurementNumber(value * 2.54)} cm`
      case "yd":
      case "yard":
      case "yards":
        return `${formatMeasurementNumber(value * 0.9144)} m`
      case "km²":
      case "km2":
      case "sq km":
        return `${formatMeasurementNumber(value * 0.386102)} mi²`
      case "mi²":
      case "mi2":
      case "sq mi":
        return `${formatMeasurementNumber(value * 2.58999)} km²`
      case "m²":
      case "m2":
      case "sq m":
        return `${formatMeasurementNumber(value * 10.7639)} ft²`
      case "ft²":
      case "ft2":
      case "sq ft":
        return `${formatMeasurementNumber(value * 0.092903)} m²`
      case "ha":
      case "hectare":
      case "hectares":
        return `${formatMeasurementNumber(value * 2.47105)} acres`
      case "acre":
      case "acres":
        return `${formatMeasurementNumber(value * 0.404686)} ha`
      case "kg":
      case "kilogram":
      case "kilograms":
        return `${formatMeasurementNumber(value * 2.20462)} lb`
      case "lb":
      case "lbs":
      case "pound":
      case "pounds":
        return `${formatMeasurementNumber(value * 0.453592)} kg`
      case "g":
      case "gram":
      case "grams":
        return `${formatMeasurementNumber(value * 0.035274)} oz`
      case "oz":
      case "ounce":
      case "ounces":
        return `${formatMeasurementNumber(value * 28.3495)} g`
      case "l":
      case "litre":
      case "litres":
      case "liter":
      case "liters":
        return `${formatMeasurementNumber(value * 0.264172)} US gal`
      case "us gal":
      case "gallon":
      case "gallons":
        return `${formatMeasurementNumber(value * 3.78541)} L`
      default:
        return null
    }
  })()

  return converted ? `${primary} · ${converted}` : null
}

export function isDualMeasurementUnit(unit?: string | null): boolean {
  return Boolean(unit && formatDualMeasurement(1, unit))
}

export function formatValue(value: number, unit?: string, prefix?: boolean): string {
  if (unit?.trim().toLocaleLowerCase("en-US") === "year") {
    return Math.round(value).toLocaleString("en-US", { useGrouping: false })
  }

  if (unit && !prefix) {
    const dualMeasurement = formatDualMeasurement(value, unit)
    if (dualMeasurement) return dualMeasurement
  }

  let formatted: string

  if (value >= 1000000000) {
    formatted = (value / 1000000000).toFixed(1) + 'B'
  } else if (value >= 1000000) {
    formatted = (value / 1000000).toFixed(1) + 'M'
  } else if (value >= 1000) {
    formatted = (value / 1000).toFixed(1) + 'K'
  } else {
    formatted = value.toLocaleString()
  }

  if (unit) {
    return prefix ? `${unit}${formatted}` : `${formatted} ${unit}`
  }

  return formatted
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// Unbiased Fisher-Yates shuffle; returns a new array.
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Serialize JSON-LD for a <script> tag. JSON.stringify does not escape "<",
// so user-supplied strings containing "</script>" would otherwise break out
// of the tag and execute as HTML.
//
// Several nodes go into one @graph object, not a top-level array: Safari
// readers and extensions call data["@context"].toLowerCase() on each block
// and throw on an array.
export function safeJsonLd(data: unknown): string {
  const value = Array.isArray(data)
    ? {
        "@context": "https://schema.org",
        // JSON.stringify drops the undefined per-node @context.
        "@graph": data.map((node: object) => ({ ...node, "@context": undefined })),
      }
    : data
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
}

export function getDailyDate(): Date {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}
